import { z } from 'zod'

// STORY-030: Types de scenarios de relance
export const reminderTypeEnum = z.enum([
  'devis_pending',      // Devis en attente de signature
  'invoice_unpaid',     // Facture envoyee mais non payee
  'invoice_overdue',    // Facture en retard de paiement
  'signature_pending',  // Signature en attente
  'question_pending',   // Question client en attente de reponse
  'task_due',           // Tâche arrive à échéance
])

export type ReminderType = z.infer<typeof reminderTypeEnum>

// Priorite de la relance
export const reminderPriorityEnum = z.enum([
  'low',      // Basse - delai de 15 jours
  'medium',   // Moyenne - delai de 7 jours
  'high',     // Haute - delai de 3 jours
  'urgent',   // Urgente - delai de 1 jour
])

export type ReminderPriority = z.infer<typeof reminderPriorityEnum>

// Statut de la relance
export const reminderStatusEnum = z.enum([
  'pending',    // En attente d'envoi
  'scheduled',  // Programmee
  'sent',       // Envoyee
  'cancelled',  // Annulee
  'completed',  // Terminee (reponse recue)
])

export type ReminderStatus = z.infer<typeof reminderStatusEnum>

// Configuration des delais par type de relance (en jours)
export interface ReminderDelayConfig {
  initial: number       // Delai avant premiere relance
  escalation1: number   // Delai avant deuxieme relance (J+3)
  escalation2: number   // Delai avant troisieme relance (J+7)
  escalation3: number   // Delai avant derniere relance (J+15)
  maxReminders: number  // Nombre maximum de relances
}

// Configuration par defaut des delais selon le type
export const defaultDelayConfigs: Record<ReminderType, ReminderDelayConfig> = {
  devis_pending: {
    initial: 3,
    escalation1: 3,
    escalation2: 7,
    escalation3: 15,
    maxReminders: 4,
  },
  invoice_unpaid: {
    initial: 7,
    escalation1: 3,
    escalation2: 7,
    escalation3: 15,
    maxReminders: 4,
  },
  invoice_overdue: {
    initial: 1,
    escalation1: 3,
    escalation2: 7,
    escalation3: 15,
    maxReminders: 4,
  },
  signature_pending: {
    initial: 2,
    escalation1: 3,
    escalation2: 7,
    escalation3: 15,
    maxReminders: 4,
  },
  question_pending: {
    initial: 1,
    escalation1: 2,
    escalation2: 5,
    escalation3: 10,
    maxReminders: 4,
  },
  task_due: {
    initial: 1,        // 1 jour avant échéance
    escalation1: 1,    // 1 jour après échéance
    escalation2: 3,    // 3 jours après
    escalation3: 7,    // 7 jours après
    maxReminders: 4,
  },
}

// Schema de relance
export const reminderSchema = z.object({
  id: z.string().uuid().optional(),
  type: reminderTypeEnum,
  priority: reminderPriorityEnum.default('medium'),
  status: reminderStatusEnum.default('pending'),

  // Reference vers l'entite concernee
  reference_type: z.enum(['devis', 'facture', 'contact', 'projet']),
  reference_id: z.string().uuid(),

  // Contact cible
  contact_id: z.string().uuid(),
  contact_email: z.string().email(),
  contact_name: z.string(),

  // Planification
  scheduled_at: z.string(),  // Date d'envoi prevue
  sent_at: z.string().nullable().optional(),

  // Contenu
  subject: z.string(),
  message: z.string().nullable().optional(),

  // Escalade
  escalation_level: z.number().min(0).max(4).default(0),
  parent_reminder_id: z.string().uuid().nullable().optional(),

  // Metadata
  created_by: z.string().uuid().nullable().optional(),
  notes: z.string().nullable().optional(),
})

export type Reminder = z.infer<typeof reminderSchema> & {
  id: string
  created_at: string
  updated_at: string
  contact?: {
    id: string
    nom: string
    prenom: string
    email: string
    entreprise?: string
  }
  reference?: {
    id: string
    numero?: string
    titre?: string
  }
}

export type ReminderFormData = z.infer<typeof reminderSchema>

// Filtres pour la liste des relances
export interface ReminderFilters {
  search?: string
  type?: ReminderType
  priority?: ReminderPriority
  status?: ReminderStatus
  dateFrom?: string
  dateTo?: string
  contactId?: string
  page?: number
  limit?: number
}

// Labels pour l'UI
export const reminderTypeLabels: Record<ReminderType, string> = {
  devis_pending: 'Devis en attente',
  invoice_unpaid: 'Facture non payee',
  invoice_overdue: 'Facture en retard',
  signature_pending: 'Signature en attente',
  question_pending: 'Question en attente',
  task_due: 'Tâche à échéance',
}

export const reminderPriorityLabels: Record<ReminderPriority, string> = {
  low: 'Basse',
  medium: 'Moyenne',
  high: 'Haute',
  urgent: 'Urgente',
}

export const reminderStatusLabels: Record<ReminderStatus, string> = {
  pending: 'En attente',
  scheduled: 'Programmee',
  sent: 'Envoyee',
  cancelled: 'Annulee',
  completed: 'Terminee',
}

// Couleurs des badges par priorite
export const reminderPriorityColors: Record<ReminderPriority, { variant: 'mint' | 'gold' | 'brown' | 'red' | 'default' }> = {
  low: { variant: 'default' },
  medium: { variant: 'gold' },
  high: { variant: 'brown' },
  urgent: { variant: 'red' },
}

// Couleurs des badges par statut
export const reminderStatusColors: Record<ReminderStatus, { variant: 'mint' | 'gold' | 'brown' | 'beige' | 'red' | 'default' }> = {
  pending: { variant: 'gold' },
  scheduled: { variant: 'beige' },
  sent: { variant: 'mint' },
  cancelled: { variant: 'default' },
  completed: { variant: 'mint' },
}

// Couleurs des badges par type
export const reminderTypeColors: Record<ReminderType, { variant: 'mint' | 'gold' | 'brown' | 'beige' | 'red' | 'blue' | 'default' }> = {
  devis_pending: { variant: 'gold' },
  invoice_unpaid: { variant: 'brown' },
  invoice_overdue: { variant: 'red' },
  signature_pending: { variant: 'beige' },
  question_pending: { variant: 'blue' },
}

// Stats des relances
export interface ReminderStats {
  total: number
  pending: number
  scheduled: number
  sent: number
  urgentes: number
}

// Type pour la creation d'une relance automatique
export interface CreateAutoReminderData {
  type: ReminderType
  reference_type: 'devis' | 'facture' | 'contact' | 'projet'
  reference_id: string
  contact_id: string
  contact_email: string
  contact_name: string
  subject: string
  message?: string
  scheduled_at?: string
  priority?: ReminderPriority
}

// Type pour les notifications de relances
export interface ReminderNotification {
  id: string
  reminder_id: string
  type: 'reminder_due' | 'reminder_sent' | 'reminder_escalated' | 'reminder_response'
  title: string
  message: string
  read: boolean
  created_at: string
  reminder?: Reminder
}
